import { useState } from 'react';
import LandingLayout from '@/components/layout/LandingLayout';
import { SEO } from '@/components/common/SEO';
import { Mail, Shield, Users, GitPullRequest, AlertTriangle, Target, BarChart3, UserCheck } from 'lucide-react';
import NetworkGraph from '@/components/charts/NetworkGraph';
import { MOCK_REVIEW_PAIRS, MOCK_GHOST_AND_STARS } from '@/data/sampleReportData';
import { api } from '@/lib/api/core';
import LeadCaptureModal from '@/components/landing/LeadCaptureModal';
import { logError } from '@/lib/logger';
import { trackCtaClick } from '@/lib/analytics';

const executiveHighlights = [
  {
    title: 'Deploy Frequency',
    value: '4.7/week',
    benchmark: 'Top 25%',
    change: '+18% vs last quarter',
    description: 'Ship cadence is above peers; maintain headcount to keep roadmap velocity.',
  },
  {
    title: 'Lead Time for Changes',
    value: '4.8 days',
    benchmark: 'Needs improvement',
    change: '+12%',
    description: 'Approval-to-merge delays add 3.2 days; automate approvals and remove queue blocks.',
  },
  {
    title: 'Change Failure Rate',
    value: '12%',
    benchmark: 'On target',
    change: '-5%',
    description: 'CI flake rate dropped after test suite cleanup-use this win in your board update.',
  },
  {
    title: 'Mean Time to Recovery',
    value: '2.3h',
    benchmark: 'Best in class',
    change: '-1.1h',
    description: 'Incidents are resolved quickly thanks to auto-rollback and clearer on-call ownership.',
  },
];

const vpTalkingPoints = [
  {
    title: 'Headcount Justification',
    detail:
      'Cycle time improved 22% after onboarding the new review pod. Cutting senior reviewers would slow critical initiatives by ~3 weeks.',
  },
  {
    title: 'Investment Ask',
    detail:
      'Two automation engineers could remove the 3.2-day approval bottleneck and unlock $1.2M ARR in delayed features.',
  },
  {
    title: 'Risk Mitigations',
    detail: 'Security review completed in 4 days. Controls mapped to the SOC 2 Trust Services Criteria, GitHub scopes remain read-only.',
  },
];

const managerActions = [
  {
    label: '36 PRs have been waiting over 48 hours for a first review.',
    action: 'Rebalance to relieve Aidan (9 reviews) and pair mid-level reviewers.',
  },
  {
    label: 'Approval-to-merge accounts for 79% of cycle time (777h).',
    action: 'Enable auto-merge for green builds, enforce "merge within 24h" playbook.',
  },
  {
    label: 'Test failure rate jumped to 20%.',
    action: 'Pin flaky suites and require reruns before merge; see CI detail in Dashboard -&gt; Quality.',
  },
];

const riskInsights = [
  {
    title: 'Hotspot: billing/calculations.py',
    detail: '43 changes / 2 owners this quarter. 38% of incidents touch this file. Assign secondary reviewers.',
  },
  {
    title: 'Knowledge silo forming in analytics/report.py',
    detail: '73% of edits by one contributor. Rotate ownership before PTO.',
  },
  {
    title: '1 risky PR merged without approval',
    detail: 'Add branch protection to prevent bypass during incident response.',
  },
];

const spaceHighlights = [
  { label: 'Satisfaction', value: '53 / 100', note: 'Proxy based on PR comment sentiment' },
  { label: 'Communication', value: '69 / 100', note: 'Reviewer diversity trending down week over week' },
  { label: 'Efficiency', value: '50 / 100', note: 'Context switching stable; biggest drag is merge queue' },
];

const securityPoints = [
  'Security controls aligned with the SOC 2 Trust Services Criteria',
  'Read-only GitHub access - we never clone your repository or read your files',
  'GitHub tokens encrypted at the application layer',
];

const personaSections = [
  {
    title: 'For VPs & Directors',
    bullets: [
      'Connect DORA trends directly to ARR initiatives so you can defend roadmap budgets.',
      'SPACE metrics reveal engagement and burnout risks before HR escalation.',
      'Export a board-ready deck with benchmarks and next bets in two clicks.',
    ],
  },
  {
    title: 'For Engineering Managers',
    bullets: [
      'Live backlog badges show which PRs violate review SLAs so you can unblock flow.',
      'Review network maps highlight overloaded seniors and neglected contributors.',
      'File Hotspots and Risky Changes make it easy to focus refactors on high-churn code.',
    ],
  },
];

const heroProofPoints = [
  {
    label: 'Queue debt',
    metric: '777h',
    detail: 'Approval-to-merge created last sprint',
  },
  {
    label: 'Breached SLAs',
    metric: '36 PRs',
    detail: 'Still waiting over 48 hours for first review',
  },
];

const cycleTimeStack = [
  { label: 'Coding & build', hours: 42, color: 'bg-emerald-400' },
  { label: 'Waiting for review', hours: 76, color: 'bg-amber-400' },
  { label: 'In review', hours: 31, color: 'bg-sky-400' },
  { label: 'Merge & deploy', hours: 18, color: 'bg-purple-400' },
];

export default function SampleReportPage() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const handleGitHubLogin = async () => {
    trackCtaClick({ location: 'sample_report' });
    try {
      setIsConnecting(true);
      const { authorization_url } = await api.getGitHubAuthUrl();
      window.location.href = authorization_url;
    } catch (error) {
      logError('Failed to get GitHub auth URL:', error);
      setIsConnecting(false);
    }
  };

  const totalCycleHours = cycleTimeStack.reduce((sum, stage) => sum + stage.hours, 0);

  return (
    <LandingLayout>
      <SEO />
      <div className="max-w-6xl lg:max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 space-y-12">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-3xl p-8 md:p-12 shadow-2xl shadow-primary-900/10">
          <div className="grid gap-10 lg:gap-16 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <div className="space-y-8">
              <div className="inline-flex flex-col text-xs uppercase tracking-[0.3em] text-primary-200">
                <span>Executive Brief · Acme Corp</span>
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl font-bold">Engineering Health Score: 81 / 100</h1>
                <p className="text-lg text-gray-200 max-w-3xl">
                  Your delivery engine sits in the top quartile for deploy cadence but loses 3+ days in approval queues.
                  Use this snapshot to defend headcount, prioritize automation, and highlight risk mitigation in the next board meeting.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/40 p-6 space-y-4">
                <div className="text-sm text-gray-200">
                  <p className="font-semibold text-white">Activate CodePulse on your terms</p>
                  <p className="text-white/70">Instant, read-only GitHub connect or a tailored exec briefing—no mandatory calls.</p>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setIsLeadModalOpen(true)}
                    className="inline-flex items-center justify-center rounded-lg bg-primary-500 hover:bg-primary-400 px-6 py-3 font-semibold shadow-lg"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Schedule Executive Briefing
                  </button>
                  <button
                    onClick={handleGitHubLogin}
                    disabled={isConnecting}
                    className="inline-flex items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                  >
                    {isConnecting ? 'Preparing...' : 'Connect GitHub (Self-serve)'}
                  </button>
                  <a
                    href="/engineering-code-intelligence"
                    className="inline-flex items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-semibold"
                  >
                    View Landing Page
                  </a>
                </div>
                <div className="text-[11px] text-gray-300 space-y-1">
                  <p>No credit card, no agent. SOC 2-aligned controls with read-only GitHub access.</p>
                  <p>Full audit logs for every sync. Connect and get this report with your own data in minutes.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {heroProofPoints.map((point) => (
                  <div key={point.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <p className="text-[11px] uppercase text-white/70 tracking-[0.2em]">{point.label}</p>
                    <p className="text-3xl font-semibold mt-1">{point.metric}</p>
                    <p className="text-xs text-gray-300 mt-1">{point.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="w-full bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm uppercase tracking-[0.3em] text-gray-300">Cycle Time Breakdown</h3>
                    <p className="text-2xl font-semibold mt-1">5.1 days median</p>
                    <p className="text-xs text-amber-200 mt-1">+3.2 days waiting for approval vs SLA</p>
                  </div>
                  <div className="text-right text-xs text-gray-300">
                    <p>Target</p>
                    <p className="text-white text-lg font-semibold">3 days</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex h-4 overflow-hidden rounded-full bg-black/30 border border-white/10">
                    {cycleTimeStack.map((stage) => (
                      <div
                        key={stage.label}
                        className={`${stage.color}`}
                        style={{ width: `${(stage.hours / totalCycleHours) * 100}%` }}
                        title={`${stage.label} • ${stage.hours}h`}
                      />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3 text-[11px] text-gray-200">
                    {cycleTimeStack.map((stage) => (
                      <div key={stage.label} className="inline-flex items-center gap-1">
                        <span className={`w-2.5 h-2.5 rounded-full ${stage.color}`} />
                        <span>
                          {stage.label} ({stage.hours}h)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="w-full bg-white/5 border border-white/10 rounded-3xl p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm uppercase tracking-[0.3em] text-gray-300">DORA Snapshot</h3>
                  <span className="text-[11px] text-gray-300">Week ending Dec 29</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-white">
                  {executiveHighlights.map((item) => (
                    <div key={item.title} className="bg-black/20 border border-white/5 rounded-2xl p-4 space-y-1">
                      <p className="text-xs text-gray-300 uppercase">{item.title}</p>
                      <p className="text-2xl font-semibold">{item.value}</p>
                      <p className="text-xs text-primary-200 mt-1">{item.benchmark}</p>
                      <p className="text-xs text-gray-400 mt-2">{item.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-300 mt-4">
                  Benchmarks follow DORA research. CodePulse tracks deploys, lead time, CFR, and MTTR automatically once GitHub connects.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-6">
          <div className="flex items-center gap-3">
            <Target className="w-8 h-8 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Board Talking Points</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {vpTalkingPoints.map((point) => (
              <div key={point.title} className="p-5 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
                <h3 className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                  {point.title}
                </h3>
                <p className="mt-2 text-gray-800 dark:text-gray-200 text-sm">{point.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-6">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Persona Outcomes</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {personaSections.map((persona) => (
              <div key={persona.title} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-gray-50 dark:bg-gray-900">
                <p className="text-sm uppercase tracking-[0.3em] text-primary-600 dark:text-primary-400">{persona.title}</p>
                <ul className="mt-4 space-y-3 text-gray-700 dark:text-gray-200 text-sm">
                  {persona.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-2">
                      <span className="text-primary-500 mt-1">-</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8">
            <div className="flex items-center gap-3 mb-4">
              <GitPullRequest className="w-7 h-7 text-primary-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Review Network Snapshot</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Who's blocking whom before sprint review.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-800">
              <NetworkGraph pairs={MOCK_REVIEW_PAIRS} height={320} />
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-6 text-sm text-gray-700 dark:text-gray-200">
              <div>
                <p className="text-xs uppercase text-gray-500">Reviews last 7 days</p>
                <p className="text-xl font-semibold">17</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Overloaded reviewers</p>
                <p className="text-xl font-semibold text-amber-600">3</p>
                <p className="text-xs text-gray-500">Aidan (9), Nathan (5), Jzaynal (2)</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">PRs waiting over 48h</p>
                <p className="text-xl font-semibold text-red-600">36</p>
                <p className="text-xs text-gray-500">Auto-generated alert inside CodePulse</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Manager Action List</h2>
            </div>
            <ul className="space-y-4 text-sm text-gray-700 dark:text-gray-200">
              {managerActions.map((item) => (
                <li key={item.label} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900">
                  <p className="font-semibold">{item.label}</p>
                  <p className="text-xs text-gray-500 mt-2">{item.action}</p>
                </li>
              ))}
            </ul>
            <a href="/pull-requests" className="text-primary-600 dark:text-primary-400 text-sm font-semibold">
              Jump into Pull Request list -&gt;
            </a>
          </div>
        </section>

        <section className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-6">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-primary-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">SPACE Framework Pulse</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Holistic view of Satisfaction, Performance, Activity, Communication, and Efficiency.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {spaceHighlights.map((highlight) => (
              <div key={highlight.label} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-sm uppercase text-gray-500">{highlight.label}</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{highlight.value}</p>
                <p className="text-xs text-gray-500 mt-2">{highlight.note}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Satisfaction scores shown are estimated via PR sentiment. Connect Developer Surveys to replace with real pulse data.
          </p>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-4">
            <div className="flex items-center gap-3">
              <Shield className="w-7 h-7 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Security & Trust</h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Every sample includes the security briefing your procurement lead will ask for. CodePulse never writes to your repos, and we provide a complete audit log for every data sync.
            </p>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
              {securityPoints.map((point) => (
                <li key={point} className="flex gap-2">
                  <span className="text-primary-500 mt-1">-</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <a href="/security" className="text-primary-600 dark:text-primary-400 text-sm font-semibold">
              View the full security posture -&gt;
            </a>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Risk & Hotspots</h2>
            </div>
            <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
              {riskInsights.map((risk) => (
                <li key={risk.title} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900">
                  <p className="font-semibold">{risk.title}</p>
                  <p className="text-xs text-gray-500 mt-2">{risk.detail}</p>
                </li>
              ))}
            </ul>
            <a href="/file-hotspots" className="text-primary-600 dark:text-primary-400 text-sm font-semibold">
              Inspect File Hotspots -&gt;
            </a>
          </div>
        </section>

        <section className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 space-y-6">
          <div className="flex items-center gap-3">
            <UserCheck className="w-8 h-8 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Team Capacity & Contributions</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Identify outliers in your engineering team. Spot "Ghosts" (low output, high cost) and "Hidden Stars" (high impact, low recognition).
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 uppercase font-medium">
                <tr>
                  <th className="px-4 py-3 rounded-l-lg">Developer</th>
                  <th className="px-4 py-3">Primary Lang</th>
                  <th className="px-4 py-3 text-right">Daily LOC</th>
                  <th className="px-4 py-3 text-right">PRs (90d)</th>
                  <th className="px-4 py-3 text-right">Reviews Given</th>
                  <th className="px-4 py-3 rounded-r-lg">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {MOCK_GHOST_AND_STARS.map((dev, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-4 font-medium text-gray-900 dark:text-white">
                      {dev.name}
                      {dev.type === 'ghost' && <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Ghost?</span>}
                      {dev.type === 'star' && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Star</span>}
                    </td>
                    <td className="px-4 py-4 text-gray-600 dark:text-gray-400">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-800 font-mono">
                        {dev.primary_lang}
                      </span>
                    </td>
                    <td className={`px-4 py-4 text-right font-mono ${dev.daily_loc < 20 ? 'text-red-600 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                      {dev.daily_loc}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-700 dark:text-gray-300">{dev.pr_count}</td>
                    <td className="px-4 py-4 text-right text-gray-700 dark:text-gray-300">{dev.review_count}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        dev.status === 'At Risk' 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' 
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {dev.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200 flex gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p>
              <strong>Anomaly Detected:</strong> "Kevin Park" is averaging 17 lines of code per day. 
              This falls below the 10th percentile for your organization.
            </p>
          </div>
        </section>

        <section className="bg-primary-700 text-white rounded-3xl p-10 text-center space-y-4">
          <p className="text-sm uppercase tracking-[0.3em] text-white/70">Next Step</p>
          <h2 className="text-3xl md:text-4xl font-bold">
            Turn this sample into your live Engineering Health Briefing
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto">
            Connect GitHub, sync the last 6 months of history, and we'll deliver your own executive briefing, risk radar, and manager action plan inside of 7 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => setIsLeadModalOpen(true)}
              className="inline-flex items-center justify-center rounded-lg bg-white text-primary-700 font-semibold px-8 py-4 shadow-lg hover:bg-primary-50 transition"
            >
              Schedule Briefing
            </button>
            <button
              onClick={handleGitHubLogin}
              disabled={isConnecting}
              className="inline-flex items-center justify-center rounded-lg border border-white/60 px-8 py-4 font-semibold text-white hover:bg-white/10 transition disabled:opacity-60"
            >
              {isConnecting ? 'Preparing...' : 'Connect GitHub Now'}
            </button>
            <a
              href="/engineering-code-intelligence"
              className="inline-flex items-center justify-center rounded-lg border border-white/60 px-8 py-4 font-semibold text-white hover:bg-white/10 transition"
            >
              Explore Landing Page
            </a>
          </div>
          <p className="text-xs text-white/70">No agents. SOC 2-aligned controls. Read-only GitHub scopes.</p>
        </section>
      </div>
      <LeadCaptureModal open={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} onConnectNow={handleGitHubLogin} />
    </LandingLayout>
  );
}
